import { del, get, post, put } from '../request';

/**
 * 排班管理（/shift，v2.1，docs/shift-management.md）：
 * 班次模板 / 排班实例 / 周模板 / 节假日 / 对账。
 */
export const CHANNEL_MODES = [
  { label: '全渠道（电话+在线）', value: 'mixed' },
  { label: '仅电话', value: 'voice_only' },
  { label: '仅在线', value: 'chat_only' },
];

export function channelModeLabel(v?: string): string {
  const m = CHANNEL_MODES.find((o) => o.value === v);
  return m ? m.label : (v || '-');
}

/** 全局事件名：渠道模式变更广播（BasicLayout 切换后通知其他订阅者，如工作台「今日班次」卡） */
export const CHANNEL_MODES_EVENT = 'bcc:channel-mode-changed';

/** 工作台界面切换事件（channel 互斥开关 → 页面切「电话 / 在线」界面，无需跳路由） */
export const WORKBENCH_SURFACE_EVENT = 'bcc:workbench-surface';
export type WorkbenchSurface = 'phone' | 'online';

/**
 * 最近一次拿到的当日渠道（`effective_mode`）：顶栏开关拉取后写入，工作台页面挂载时读取。
 * 两个消费方挂载先后不确定（页面是懒加载子路由），而 WORKBENCH_SURFACE_EVENT 是一次性事件，
 * 事件早于挂载就没人接。页面挂载时读这里做一次幂等落位，可摆脱该时序依赖且不重复请求接口。
 */
let effectiveModeCache: string | null = null;

export function getCachedEffectiveMode(): string | null {
  return effectiveModeCache;
}

export function setCachedEffectiveMode(mode?: string | null) {
  effectiveModeCache = mode || null;
}

/** channel mode ↔ 会话/电话两路开关的映射（坐席侧只关心这两个渠道；混合=两路均开） */
export function modeToSwitches(mode?: string): { chat: boolean; voice: boolean } {
  if (mode === 'chat_only') return { chat: true, voice: false };
  if (mode === 'voice_only') return { chat: false, voice: true };
  // 'mixed' / 缺省 / 未知值：按全渠道处理
  return { chat: true, voice: true };
}

/** 两路开关 → channel mode；同时关闭不允许（返回 null 提示调用方拒绝） */
export function switchesToMode(chat: boolean, voice: boolean): string | null {
  if (chat && voice) return 'mixed';
  if (chat && !voice) return 'chat_only';
  if (!chat && voice) return 'voice_only';
  return null; // 双开关闭不允许
}

/** 班次模板 */
export interface ShiftTemplate {
  id: number;
  name: string;
  start_time: string; // HH:MM
  end_time: string; // HH:MM
  work_mode: string;
  skill_group_id?: number | null;
  is_night?: boolean;
  overtime_pay_hint?: string;
  color?: string;
  category?: string; // 班次分类 morning|middle|evening|night|short，空=自动归类
  enabled?: boolean;
  remark?: string;
}

/** 班次分类（v2.2） */
export type ShiftCategory = 'morning' | 'middle' | 'evening' | 'night' | 'short';

export const SHIFT_CATEGORIES: { label: string; value: ShiftCategory | ''; color: string }[] = [
  { label: '早班', value: 'morning', color: 'arcoblue' },
  { label: '中班', value: 'middle', color: 'cyan' },
  { label: '晚班', value: 'evening', color: 'orange' },
  { label: '大夜班', value: 'night', color: 'red' },
  { label: '短班', value: 'short', color: 'gold' },
];

export function shiftCategoryInfo(v?: string | null): { label: string; color: string } {
  const m = SHIFT_CATEGORIES.find((o) => o.value === v);
  return m ? { label: m.label, color: m.color } : { label: '自动归类', color: 'gray' };
}

/** 休假/休息日类型（v2.2） */
export type LeaveType = 'rest' | 'annual' | 'personal' | 'sick' | 'comp' | 'training';

export const LEAVE_TYPES: { label: string; value: LeaveType; color: string }[] = [
  { label: '休息日', value: 'rest', color: 'arcoblue' },
  { label: '年假', value: 'annual', color: 'green' },
  { label: '事假', value: 'personal', color: 'orange' },
  { label: '病假', value: 'sick', color: 'red' },
  { label: '调休', value: 'comp', color: 'gold' },
  { label: '外出培训', value: 'training', color: 'cyan' }, // 6 类休假互异且无紫
];

export function leaveTypeInfo(v?: string): { label: string; color: string } {
  const m = LEAVE_TYPES.find((o) => o.value === v);
  return m ? { label: m.label, color: m.color } : { label: v || '休息日', color: 'arcoblue' };
}

/** 排班实例（日历格） */
export interface AgentShift {
  id: number;
  agent_id: number;
  agent_name?: string;
  extension?: string;
  skill_group_id?: number;
  shift_date: string;
  shift_template_id: number;
  start_time: string;
  end_time: string;
  work_mode: string;
  effective_mode?: string;
  effective_skill_group_id?: number | null;
  template_name?: string;
  template_color?: string;
  is_night?: boolean;
  source?: string;
  remark?: string; // 排班备注（v2.2）
}

/** 休假/休息日登记（v2.2） */
export interface AgentLeave {
  id: number;
  agent_id: number;
  agent_name?: string;
  extension?: string;
  leave_date: string; // YYYY-MM-DD
  leave_type: LeaveType;
  remark?: string;
  created_by?: number;
  created_at?: string;
}

export interface WeekShiftDayInput {
  weekday: number; // 1-7
  shift_template_id: number; // 0=休息
  work_mode?: string | null;
  skill_group_id?: number | null;
}

export interface WeekShiftTemplate {
  id: number;
  name: string;
  enabled?: boolean;
  days?: WeekShiftDayInput[];
  cycle_mode?: 'single' | 'biweekly' | 'once' | ''; // v2.2 单周/双周轮换/不循环
  cycle_group?: string; // v2.2 biweekly 轮换组名（A/B 共用）
  cycle_role?: 'A' | 'B' | ''; // v2.2 biweekly 角色
}

/** 周模板循环模式 */
export const WEEK_CYCLE_MODES = [
  { label: '单周循环', value: 'single' },
  { label: '双周轮换（A·B）', value: 'biweekly' },
  { label: '不循环（仅一次）', value: 'once' },
];

export function cycleModeLabel(m?: string): string {
  return WEEK_CYCLE_MODES.find((o) => o.value === m)?.label || '单周循环';
}

export interface Holiday {
  id: number;
  date: string;
  type: 'holiday' | 'workday';
  name: string;
  year?: number;
  remark?: string;
}

export interface AttendanceRow {
  agent_id: number;
  agent_name: string;
  extension: string;
  shift_date: string;
  template_name?: string;
  start_time: string;
  end_time: string;
  plan_minutes: number;
  on_duty_sec: number;
  first_on_duty?: string | null;
  last_on_duty?: string | null;
  late_dur_sec?: number; // v2.2 迟到时长
  overtime_sec?: number; // v2.2 加班时长
  leave_type?: string; // v2.2 休假类型（休假行）
  remark?: string; // v2.2 排班/休假备注
  rate: number;
  status: 'normal' | 'late' | 'early' | 'absent' | 'leave' | 'overtime';
}

/** GET /shift/templates?enabled=true */
export async function listShiftTemplates(enabledOnly = false): Promise<ShiftTemplate[]> {
  return get('/shift/templates', { enabled: enabledOnly || undefined });
}

export function createShiftTemplate(v: Partial<ShiftTemplate>) {
  return post('/shift/templates', v);
}

export function updateShiftTemplate(id: number, v: Partial<ShiftTemplate>) {
  return put(`/shift/templates/${id}`, v);
}

export function deleteShiftTemplate(id: number) {
  return del(`/shift/templates/${id}`);
}

/** GET /shift/shifts?dateFrom=&dateTo=&agentId=&skillGroupId= */
export async function listAgentShifts(dateFrom: string, dateTo: string, params?: { agentId?: number; skillGroupId?: number }): Promise<AgentShift[]> {
  return get('/shift/shifts', { dateFrom, dateTo, ...(params || {}) });
}

/** POST /shift/shifts 批量排班（可带备注） */
export function batchAssignShifts(v: { date: string; shift_template_id: number; agent_ids: number[]; remark?: string }) {
  return post('/shift/shifts', v);
}

/** POST /shift/shifts/adhoc 临时班次（单次班次，不落模板库） */
export function assignAdhocShifts(v: {
  date: string;
  start_time: string;
  end_time: string;
  work_mode?: string;
  skill_group_id?: number | null;
  agent_ids: number[];
  remark?: string;
}) {
  return post('/shift/shifts/adhoc', v);
}

/** POST /shift/copy-week 复制上周排班 */
export function copyWeek(v: { from_start: string; to_start: string; agent_ids?: number[] }) {
  return post('/shift/copy-week', v);
}

export function updateAgentShift(id: number, v: Partial<AgentShift>) {
  return put(`/shift/shifts/${id}`, v);
}

export function deleteAgentShift(id: number) {
  return del(`/shift/shifts/${id}`);
}

/** GET /shift/leaves 休假/休息日（日历数据源） */
export async function listAgentLeaves(dateFrom: string, dateTo: string, params?: { agentId?: number; skillGroupId?: number }): Promise<AgentLeave[]> {
  return get('/shift/leaves', { dateFrom, dateTo, ...(params || {}) });
}

/** POST /shift/leaves 批量设置休假/休息日 */
export function createAgentLeaves(v: { leave_date: string; leave_type: string; remark?: string; agent_ids: number[]; clear_shifts?: boolean }) {
  return post('/shift/leaves', v);
}

export function deleteAgentLeave(id: number) {
  return del(`/shift/leaves/${id}`);
}

export async function listWeekTemplates(): Promise<WeekShiftTemplate[]> {
  return get('/shift/week-templates');
}

export async function getWeekTemplate(id: number): Promise<WeekShiftTemplate> {
  return get(`/shift/week-templates/${id}`);
}

export function createWeekTemplate(v: { name: string; days: WeekShiftDayInput[]; cycle_mode?: string; cycle_group?: string; cycle_role?: string }) {
  return post('/shift/week-templates', v);
}

export function updateWeekTemplate(
  id: number,
  v: { name?: string; enabled?: boolean; days?: WeekShiftDayInput[]; cycle_mode?: string; cycle_group?: string; cycle_role?: string }
) {
  return put(`/shift/week-templates/${id}`, v);
}

export function deleteWeekTemplate(id: number) {
  return del(`/shift/week-templates/${id}`);
}

/** POST /shift/week-templates/:id/apply 套用周模板（overwrite=true 覆盖该周既有排班） */
export function applyWeekTemplate(id: number, v: { week_start: string; agent_ids?: number[]; overwrite?: boolean }) {
  return post(`/shift/week-templates/${id}/apply`, v);
}

/** POST /shift/week-templates/biweekly/apply 双周轮换批量套用（A/B 交替生成 weeks 周） */
export function applyBiweekly(v: {
  week_start: string;
  cycle_group: string;
  start_role?: 'A' | 'B';
  agent_ids?: number[];
  weeks?: number;
  overwrite?: boolean;
}) {
  return post(`/shift/week-templates/biweekly/apply`, v);
}

export async function listHolidays(year?: number): Promise<Holiday[]> {
  return get('/shift/holidays', { year: year || undefined });
}

export function createHoliday(v: { date: string; type: string; name: string; remark?: string }) {
  return post('/shift/holidays', v);
}

export function deleteHoliday(id: number) {
  return del(`/shift/holidays/${id}`);
}

/** POST /shift/holidays/import 一键导入某年国家法定节假日/调休 */
export function importHolidays(v: { year: number }) {
  return post('/shift/holidays/import', v);
}

/** GET /shift/holidays/import-meta 内置国家节假日数据的年份 */
export async function importHolidaysMeta(): Promise<{ years: number[] }> {
  return get('/shift/holidays/import-meta');
}

/** GET /shift/attendance */
export async function listAttendance(dateFrom: string, dateTo: string, params?: { agentId?: number; skillGroupId?: number }): Promise<AttendanceRow[]> {
  return get('/shift/attendance', { dateFrom, dateTo, ...(params || {}) });
}

/** GET /shift/me/today 今日班次 + 生效模式 */
export async function getMyToday(): Promise<{ shifts: AgentShift[]; effective_mode: string; effective_skill_group_id: number | null }> {
  return get('/shift/me/today');
}

/** PUT /agents/me/channel-mode 设置我的默认接线模式 */
export function setMyChannelMode(channel_mode: string) {
  return put('/agents/me/channel-mode', { channel_mode });
}
